import { useState, useEffect } from 'react';
import DollarFormat from '../format/DollarFormat';
import styles from './CalculationDetailModal.module.css';
import { CalculationVariable, CalculationVariableElement } from './CalculationDetailData';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { DataResult } from '../model/DataResult';

const layout = {
  display: 'grid',
  gridTemplateColumns: '0.25fr 1fr 1fr 6fr 6fr 1fr'
};

interface CalculationVariableContainerProps {
  isExpanded: boolean;
  variableList: CalculationVariable[];
}

export default function CalculationVariableContainer({
  isExpanded,
  variableList
}: CalculationVariableContainerProps) {
  const [allExpanded, setAllExpanded] = useState(false);

  return (
    <div className={!isExpanded ? styles.closed : ''}>
      <div style={layout} className={styles.headers}>
        <span className={styles.expanded_column} onClick={() => setAllExpanded(!allExpanded)}>
          {allExpanded ? <FaChevronDown /> : <FaChevronUp />}
        </span>
        <span>Year</span>
        <span>Element</span>
        <span>Table</span>
        <span>Column</span>
        <span className="column_align_right">Value</span>
      </div>
      <div className={styles.modal_item_container}>
        {variableList.map((variable: CalculationVariable) => (
          <CalculationVariableRow
            key={`variable-${variable.name}`}
            {...variable}
            allExpanded={allExpanded}
          />
        ))}
      </div>
    </div>
  );
}

interface CalculationVariableProps {
  name: string;
  tableDescription: string;
  beginPageRange: string;
  columnExpression: string;
  year: number;
  total: DataResult;
  variableElementList: CalculationVariableElement[];
  allExpanded: boolean;
}

function CalculationVariableRow({
  name,
  tableDescription,
  beginPageRange,
  columnExpression,
  year,
  total,
  variableElementList,
  allExpanded
}: CalculationVariableProps) {
  const [lineItemsExpanded, setLineItemsExpanded] = useState(false);

  useEffect(() => {
    setLineItemsExpanded(allExpanded);
  }, [allExpanded]);

  return (
    <>
      <div
        data-testid={`variable-${name}`}
        style={layout}
        className={styles.variable}
        onClick={() => {
          setLineItemsExpanded(!lineItemsExpanded);
        }}>
        <p className={styles.expanded_column}>
          {lineItemsExpanded ? <FaChevronDown /> : <FaChevronUp />}
        </p>
        <p>{year}</p>
        <p>{name}</p>
        <p className="truncated">{`(${beginPageRange}) ${tableDescription}`}</p>
        <p>{columnExpression}</p>
        <DollarFormat showDefaultValue={true} {...total} />
      </div>
      {lineItemsExpanded && variableElementList.map(CalculationVariableElementRow)}
    </>
  );
}

function CalculationVariableElementRow({
  lineNumber,
  variableResult: value,
  lineDescription,
  operator
}: CalculationVariableElement) {
  const hasOperator = operator && operator.trim() !== '';
  const elementsLayout = {
    display: 'grid',
    gridTemplateColumns: hasOperator
      ? '1.75fr 3fr 1fr 2fr 1fr 2fr' // when operator is present for calculated rows
      : '0.25fr 1fr 1fr 6fr 6fr 1fr' // no operator
  };
  return (
    <div
      data-testid={`element-${lineNumber}`}
      key={`element-${lineNumber}`}
      style={elementsLayout}
      className={styles.variable_element}>
      {hasOperator ? (
        <>
          <p></p>
          <p>{lineDescription}</p>
          <p>{operator}</p>
          <p>{lineNumber}</p>
          <p></p>
          <DollarFormat showDefaultValue={true} {...value} />
        </>
      ) : (
        <>
          <p></p>
          <p></p>
          <p>{lineNumber}</p>
          <p>{lineDescription}</p>
          <p></p>
          <DollarFormat showDefaultValue={true} {...value} />
        </>
      )}
    </div>
  );
}
